import React, {
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { type ApolloError, useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import {
	Box,
	type SxProps,
	type Theme,
	Stack,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { type GridSortModel } from '@mui/x-data-grid-premium';
import { captureException } from '@sentry/react';
import { format as formatDate } from 'date-fns';
import { useSnackbar } from 'notistack';

import RouteLink from '@ivy/components/atoms/RouteLink';
import {
	GridSkeleton,
	TableSkeleton,
} from '@ivy/components/molecules/ListSkeletons';
import SelectionFloater from '@ivy/components/molecules/SelectionFloater';
import ConfirmationDialog from '@ivy/components/organisms/ConfirmationDialog';
import { useRedirect } from '@ivy/components/providers/RedirectProvider';
import {
	generateNewExpirationDate,
	JOB_POSTING_EXPIRING_SOON_WINDOW_DAYS,
} from '@ivy/constants/posting';
import { gql } from '@ivy/gql/types';
import {
	type JobPostingList_SearchJobPostingsQuery,
	type JobPostingList_SearchJobPostingsQueryVariables,
} from '@ivy/gql/types/graphql';
import { displayErrorMessageToast } from '@ivy/lib/helpers/error';
import { useNotifyError } from '@ivy/lib/hooks';
import lazyRetry from '@ivy/lib/util/lazyRetry';
import { buildInternalLink } from '@ivy/lib/util/route';
import { dateFromNow } from '@ivy/lib/validation/date';

import { JobPostingStatus } from '../JobPostingTabs';

import { FilterBar, FiltersPopup, useFilterContext } from './filters';
import { type JobPostingTableProps } from './JobPostingTable';

// Since we have multiple lazyRetry imports, we need to pass a unique key to each
const jGrid = lazyRetry(
	() => import('./JobPostingGrid'),
	'JobPostingList_JobPostingGrid',
);
const jTable = lazyRetry(
	() => import('./JobPostingTable'),
	'JobPostingList_JobPostingTable',
);

const DEFAULT_PAGE_SIZE = {
	// 10 rows of 1 card
	xs: 3,
	// 10 rows of 2 cards
	sm: 10 * 2,
	// 10 rows of 3 cards
	md: 10 * 3,
	desktop: 25,
};

// We're including inactive contracts in filters + connections
export const JobPostingList_SearchJobPostingsQDoc = gql(/* GraphQL */ `
	query JobPostingList_SearchJobPostings(
		$limit: Int!
		$offset: Int!
		$orderBy: [relevant_job_posting_order_by!]
		$search: String!
		$filters: relevant_job_posting_bool_exp!
		$dateInPast: timestamp!
		$orgId: uuid!
		$expirationDate: date!
	) {
		relevantJobPostings: search_relevant_job_posting(
			where: $filters
			limit: $limit
			offset: $offset
			order_by: $orderBy
			args: { search: $search }
		) {
			id
			jobPosting: job_posting {
				id
				...JobPostingCard_JobPostingInfo
				...JobPostingTable_JobPostingInfo
			}
		}
		agg: search_relevant_job_posting_aggregate(
			where: $filters
			args: { search: $search }
		) {
			aggregate {
				count
			}
		}
		aggActive: search_relevant_job_posting_aggregate(
			where: {
				job_posting: {
					_and: [
						{ active: { _eq: true }, contract: { org_id: { _eq: $orgId } } }
					]
				}
			}
			args: { search: "" }
		) {
			aggregate {
				count
			}
		}
		aggExpiringSoon: search_relevant_job_posting_aggregate(
			where: {
				job_posting: {
					_and: [
						{
							active: { _eq: true }
							expiration_date: { _lte: $expirationDate }
							contract: { org_id: { _eq: $orgId } }
						}
					]
				}
			}
			args: { search: "" }
		) {
			aggregate {
				count
			}
		}
		aggInactive: search_relevant_job_posting_aggregate(
			where: {
				job_posting: {
					_and: [
						{
							active: { _eq: false }
							latest_publication: { unpublished_at: { _gte: $dateInPast } }
							contract: { org_id: { _eq: $orgId } }
						}
					]
				}
			}
			args: { search: "" }
		) {
			aggregate {
				count
			}
		}
	}
`);

const JobPostingList_BulkUpdateJobPostingMDoc = gql(/* GraphQL */ `
	mutation JobPostingList_BulkUpdateJobPosting(
		$jobPostingIds: [uuid!]!
		$active: Boolean!
		$expirationDate: date
	) {
		update_job_posting(
			where: { id: { _in: $jobPostingIds } }
			_set: {
				active: $active
				last_modified: "now()"
				expiration_date: $expirationDate
			}
		) {
			returning {
				id
			}
		}
	}
`);

const DEFAULT_ORDER_BY = [
	{
		job_posting: {
			latest_publication: { published_at: 'desc' },
		},
	},
	{
		job_posting: {
			id: 'asc',
		},
	},
];

interface JobPostingListProps {
	filtersPopupOpen?: boolean;
	setFiltersPopupOpen?: (newValue: boolean) => void;
	onCompletedQuery?: (val?: JobPostingList_SearchJobPostingsQuery) => void;
	onErrorQuery?: (val?: ApolloError) => void;
	searchTerm?: string;
	sx?: SxProps<Theme>;
	refetchQueries?: string[];
	where?: JobPostingList_SearchJobPostingsQueryVariables['filters'];
	orgId: string;
	tableProps?: Omit<
		JobPostingTableProps,
		| 'onChangeSort'
		| 'jobPostings'
		| 'loading'
		| 'filtering'
		| 'onEdit'
		| 'onPublish'
		| 'onUnpublish'
	>;
	selectable?: boolean;
	tabValue?: JobPostingStatus;
}

const JobPostingTable = memo(jTable);
const JobPostingGrid = memo(jGrid);

enum PostingAction {
	ACTIVATE = 'ACTIVATE',
	DEACTIVATE = 'DEACTIVATE',
	RENEW = 'RENEW',
}

const JobPostingList = ({
	onCompletedQuery,
	onErrorQuery,
	searchTerm = '',
	tableProps,
	setFiltersPopupOpen,
	filtersPopupOpen = false,
	where,
	sx,
	orgId,
	selectable,
	tabValue,
}: JobPostingListProps) => {
	const { apiFilters, filtersCount, appliedColumns } = useFilterContext();

	const { enqueueSnackbar } = useSnackbar();
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
	const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'), {
		noSsr: true,
	});
	const isMd = useMediaQuery(theme.breakpoints.between('md', 'gridBreak'), {
		noSsr: true,
	});
	const isLtMd = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
	const breakpoint = isXs ? 'xs' : isSm ? 'sm' : isMd ? 'md' : 'desktop';
	const isDesktopOrLaptop = useMediaQuery(theme.breakpoints.up('gridBreak'), {
		noSsr: true,
	});

	const redirect = useRedirect();
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE[breakpoint]);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [orderBy, setOrderBy] = useState<{ [k: string]: any }[]>([]);
	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [bulkLoading, setBulkLoading] = useState(false);
	const [bulkAction, setBulkAction] = useState<PostingAction | null>(null);
	const [singleId, setSingleId] = useState<{
		id: string;
		action: PostingAction;
	} | null>(null);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	// Reset the page whenever the underlying query changes
	useEffect(() => {
		setPage(0);
	}, [apiFilters, orderBy, setPage]);

	const [updateBulkJobPosting, { loading: updateLoading }] = useMutation(
		JobPostingList_BulkUpdateJobPostingMDoc,
		{
			refetchQueries: [
				'JobPostingList_SearchJobPostings',
				'BillingProvider_GetSubscription',
			],
			awaitRefetchQueries: true,
		},
	);

	const onPublish = async (jobPostingIds: string[]) => {
		try {
			await updateBulkJobPosting({
				variables: {
					jobPostingIds: jobPostingIds,
					active: true,
					expirationDate: generateNewExpirationDate().toISOString(),
				},
			});
		} catch (e) {
			captureException(e, {
				extra: {
					jobPostingId: jobPostingIds,
				},
			});
			displayErrorMessageToast(e);
		}
	};

	const bulkPublish = async (jobPostingIds: string[]) => {
		setBulkLoading(true);
		try {
			return await onPublish(jobPostingIds);
		} catch (e) {
			captureException(e);
			console.error(e);
			enqueueSnackbar('An error occurred, please try again.', {
				variant: 'error',
			});
		} finally {
			setSingleId(null);
			setSelectedIds([]);
			setBulkAction(null);
			setBulkLoading(false);
		}
	};

	const onUnpublish = async (jobPostingIds: string[]) => {
		try {
			await updateBulkJobPosting({
				variables: {
					jobPostingIds: jobPostingIds,
					active: false,
					expirationDate: null,
				},
			});
		} catch (e) {
			captureException(e, {
				extra: {
					jobPostingId: jobPostingIds,
				},
			});
			displayErrorMessageToast(e);
		}
	};

	const bulkUnpublish = async (jobPostingIds: string[]) => {
		setBulkLoading(true);
		try {
			return await onUnpublish(jobPostingIds);
		} catch (e) {
			captureException(e);
			console.error(e);
			enqueueSnackbar('An error occurred, please try again.', {
				variant: 'error',
			});
		} finally {
			setSingleId(null);
			setSelectedIds([]);
			setBulkAction(null);
			setBulkLoading(false);
		}
	};

	const onEdit = (jobPostingId: string) => {
		redirect(
			buildInternalLink(RouteLink.routes.JOB_POSTING_EDIT, {
				postingId: jobPostingId,
			}),
			{
				state: {
					backNav: {
						target: 'jobs',
					},
				},
			},
		);
	};

	// Reset page and orderBy upon search
	useEffect(() => {
		setPage(0);
		if (searchTerm) {
			// Default the search to sort by relevance.  The user can change the order afterwards.
			setOrderBy([
				{
					relevance: 'desc',
				},
			]);
			// Relevance does not appear in the DataGrid sort model
			setSortModel([]);
		} else {
			setOrderBy([]);
			setSortModel([]);
		}
	}, [searchTerm, setPage, setOrderBy]);

	// Don't render anything while switching from mobile to desktop until state fully reset
	// Can reproduce DataGrid crash by going to mobile, scrolling down until a couple pages have loaded, and then
	// switching to desktop. DataGrid will try to scroll to an index and fail.
	const [resetting, setResetting] = useState(false);

	useEffect(() => {
		setResetting(true);
	}, [isDesktopOrLaptop]);

	useEffect(() => {
		if (!resetting) {
			return;
		}
		setPage(0);
		setPageSize(DEFAULT_PAGE_SIZE[breakpoint]);
		setOrderBy(
			searchTerm
				? [
						{
							relevance: 'desc',
						},
				  ]
				: [],
		);
		setSortModel([]);
		setResetting(false);
	}, [
		resetting,
		breakpoint,
		searchTerm,
		setPage,
		setPageSize,
		setOrderBy,
		setSortModel,
		setResetting,
	]);

	const { data, loading, error, fetchMore, networkStatus } = useQuery(
		JobPostingList_SearchJobPostingsQDoc,
		{
			variables: {
				orgId: orgId,
				dateInPast: dateFromNow({ days: -30 }),
				search: searchTerm,
				filters: {
					_and: [...apiFilters, where || {}],
				},
				orderBy: [
					...orderBy,
					// Use the default ordering as a tiebreaker
					...DEFAULT_ORDER_BY,
				],
				limit: isDesktopOrLaptop ? pageSize : (page + 1) * pageSize,
				offset: isDesktopOrLaptop ? page * pageSize : 0,
				expirationDate: dateFromNow({
					days: JOB_POSTING_EXPIRING_SOON_WINDOW_DAYS,
				}).toISOString(),
			},
			notifyOnNetworkStatusChange: true,
			fetchPolicy: 'cache-and-network',
			nextFetchPolicy: 'cache-first',
		},
	);
	useNotifyError(error);

	useEffect(() => {
		const onCompleted = (_data?: JobPostingList_SearchJobPostingsQuery) => {
			if (onCompletedQuery) onCompletedQuery(_data);
		};
		const onError = (_error: ApolloError) => {
			if (onErrorQuery) onErrorQuery(_error);
		};
		if (onCompleted || onError) {
			if (onCompleted && !loading && !error) {
				onCompleted(data);
			} else if (onError && !loading && error) {
				onError(error);
			}
		}
	}, [loading, data, error, onCompletedQuery, onErrorQuery]);

	const jobPostings = useMemo(() => {
		return data?.relevantJobPostings.map((el) => el.jobPosting!) || [];
	}, [data]);
	const totalJobPostingSelectableCount = jobPostings.length;

	const handlePageChange = useCallback(
		async (newPage: number) => {
			await fetchMore({
				variables: {
					offset: newPage * pageSize,
				},
			});
			setPage(newPage);
		},
		[fetchMore, pageSize, setPage],
	);

	const handlePageSizeChange = useCallback(
		async (newPageSize: number) => {
			await fetchMore({
				variables: {
					limit: newPageSize,
					offset: page * newPageSize,
				},
			});
			setPageSize(newPageSize);
		},
		[fetchMore, page, setPageSize],
	);

	const handleFetchPage = useCallback(async () => {
		await fetchMore({
			variables: {
				limit: pageSize,
				offset: (page + 1) * pageSize,
			},
		});

		setPage(page + 1);
	}, [fetchMore, page, pageSize, setPage]);

	const handleSortChange = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(newSortModel: GridSortModel, newOrderBy: { [k: string]: any }[]) => {
			setSortModel(newSortModel);
			setOrderBy(newOrderBy);
		},
		[setSortModel, setOrderBy],
	);

	const handleJobPostingSelection = (ids: string[]) => {
		setSelectedIds(ids);
	};
	const handleSingleJobPostingSelection =
		(action: PostingAction) => (id: string) => {
			setSingleId({ id, action });
		};

	const handleSelectAll = (jp) => {
		const postIds = jp.map((el) => el.id);
		setSelectedIds(postIds);
	};

	const handleBulkOperation = async () => {
		if (!bulkAction) {
			return;
		}
		const selectedArr = [...selectedIds];
		if (bulkAction === PostingAction.DEACTIVATE) {
			await bulkUnpublish(selectedArr);
		} else {
			// For both activation and renewal, we can use the publish operation to make sure they are
			// active and have a renewed expiration date
			await bulkPublish(selectedArr);
		}
	};

	const handleSingleOperation = async () => {
		if (!singleId) {
			return;
		}
		const selectedArr = [singleId.id];
		if (singleId.action === PostingAction.DEACTIVATE) {
			await bulkUnpublish(selectedArr);
		} else {
			// For both activation and renewal, we can use the publish operation to make sure they are
			// active and have a renewed expiration date
			await bulkPublish(selectedArr);
		}
	};
	const handleSingleCancel = () => {
		setSingleId(null);
	};

	const handleBulkClick = (_, action: string) =>
		setBulkAction(action as PostingAction);
	const handleBulkCancel = () => setBulkAction(null);

	const filtersActive = Object.values(filtersCount).some((el) => !!el);

	useEffect(() => {
		setSelectedIds([]);
		setBulkAction(null);
	}, [tabValue]);

	// Filters selectedIds to only include IDs present in the updated jobPostings list
	useEffect(() => {
		if (selectedIds.length > 0) {
			setSelectedIds(
				selectedIds.filter((id) =>
					jobPostings.some((jobPosting) => jobPosting.id === id),
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [jobPostings]);

	return (
		<React.Fragment>
			<Box sx={sx}>
				{resetting ? null : isDesktopOrLaptop ? (
					<>
						<Box>
							<Box>
								<Stack direction='row' justifyContent='space-between'>
									<FilterBar />
								</Stack>
							</Box>
						</Box>
						<Suspense
							fallback={<TableSkeleton sx={tableProps?.sx} size={pageSize} />}
						>
							<JobPostingTable
								page={page}
								onChangeSort={handleSortChange}
								sortModel={sortModel}
								jobPostings={jobPostings}
								loading={loading || bulkLoading || updateLoading}
								pageSize={pageSize}
								onPageSizeChange={handlePageSizeChange}
								onPageChange={handlePageChange}
								rowCount={data?.agg.aggregate?.count || 0}
								filtering={filtersActive || !!searchTerm}
								columnVisibilityModel={appliedColumns}
								onEdit={
									tabValue &&
									[
										JobPostingStatus.ACTIVE,
										JobPostingStatus.EXPIRING_SOON,
									].includes(tabValue)
										? onEdit
										: undefined
								}
								onPublish={
									tabValue === JobPostingStatus.INACTIVE
										? handleSingleJobPostingSelection(PostingAction.ACTIVATE)
										: undefined
								}
								onRenew={
									tabValue &&
									[
										JobPostingStatus.ACTIVE,
										JobPostingStatus.EXPIRING_SOON,
									].includes(tabValue)
										? handleSingleJobPostingSelection(PostingAction.RENEW)
										: undefined
								}
								onUnpublish={
									tabValue &&
									[
										JobPostingStatus.ACTIVE,
										JobPostingStatus.EXPIRING_SOON,
									].includes(tabValue)
										? handleSingleJobPostingSelection(PostingAction.DEACTIVATE)
										: undefined
								}
								checkboxSelection={selectable}
								onSelectionModelChange={handleJobPostingSelection}
								selectionModel={Array.from(selectedIds)}
								{...tableProps}
							/>
						</Suspense>
					</>
				) : (
					<>
						{setFiltersPopupOpen ? (
							<FiltersPopup
								open={filtersPopupOpen}
								onClose={() => setFiltersPopupOpen(false)}
							/>
						) : undefined}
						<Suspense fallback={<GridSkeleton size={pageSize} />}>
							<JobPostingGrid
								jobPostings={jobPostings}
								networkStatus={networkStatus}
								loading={loading || bulkLoading || updateLoading}
								filtersActive={filtersActive}
								onFetchPage={handleFetchPage}
								searchTerm={searchTerm}
								pageSize={pageSize}
								onCardClick={onEdit}
								count={data?.agg.aggregate?.count || 0}
								atMaxPage={
									!!data &&
									data.agg.aggregate!.count === data.relevantJobPostings.length
								}
								onEdit={
									tabValue &&
									[
										JobPostingStatus.ACTIVE,
										JobPostingStatus.EXPIRING_SOON,
									].includes(tabValue)
										? onEdit
										: undefined
								}
								onPublish={
									tabValue === JobPostingStatus.INACTIVE
										? handleSingleJobPostingSelection(PostingAction.ACTIVATE)
										: undefined
								}
								onRenew={
									tabValue &&
									[
										JobPostingStatus.ACTIVE,
										JobPostingStatus.EXPIRING_SOON,
									].includes(tabValue)
										? handleSingleJobPostingSelection(PostingAction.RENEW)
										: undefined
								}
								onUnpublish={
									tabValue &&
									[
										JobPostingStatus.ACTIVE,
										JobPostingStatus.EXPIRING_SOON,
									].includes(tabValue)
										? handleSingleJobPostingSelection(PostingAction.DEACTIVATE)
										: undefined
								}
								onSelection={handleJobPostingSelection}
								selected={Array.from(selectedIds)}
								selectable={selectable}
							/>
						</Suspense>
					</>
				)}
			</Box>
			{!!bulkAction && !!selectedIds.length && (
				<ConfirmationDialog
					open
					text={
						bulkAction === PostingAction.RENEW ? (
							<>
								Would you like to renew{' '}
								{selectedIds.length > 1
									? 'these job postings'
									: 'this job posting'}
								? The new expiration date will be{' '}
								<b>{formatDate(generateNewExpirationDate(), 'MMM d, yyyy')}</b>.
							</>
						) : (
							`Are you sure you want to ${
								bulkAction === PostingAction.DEACTIVATE
									? 'deactivate'
									: 'activate'
							} ${
								selectedIds.length > 1
									? 'these job postings'
									: 'this job posting'
							}?`
						)
					}
					onSubmit={handleBulkOperation}
					onClose={handleBulkCancel}
					loading={bulkLoading}
				/>
			)}
			{!!singleId && (
				<ConfirmationDialog
					open
					text={
						singleId.action === PostingAction.RENEW ? (
							<>
								Would you like to renew this job posting? The new expiration
								date will be{' '}
								<b>{formatDate(generateNewExpirationDate(), 'MMM d, yyyy')}</b>.
							</>
						) : (
							`Are you sure you want to ${
								singleId.action === PostingAction.DEACTIVATE
									? 'deactivate'
									: 'activate'
							} this job posting?`
						)
					}
					onSubmit={handleSingleOperation}
					onClose={handleSingleCancel}
					loading={bulkLoading}
				/>
			)}
			{selectable ? (
				<SelectionFloater
					minimized={isLtMd}
					selected={selectedIds}
					onClickSelectAll={
						totalJobPostingSelectableCount !== selectedIds.length
							? () => handleSelectAll(jobPostings)
							: undefined
					}
					onClickReset={
						totalJobPostingSelectableCount === selectedIds.length
							? () => setSelectedIds([])
							: undefined
					}
					onClick={handleBulkClick}
					options={
						tabValue === JobPostingStatus.ACTIVE
							? [
									{
										title: 'Deactivate',
										value: PostingAction.DEACTIVATE,
									},
									{
										title: 'Renew',
										value: PostingAction.RENEW,
									},
							  ]
							: tabValue === JobPostingStatus.EXPIRING_SOON
							? [
									{
										title: 'Renew',
										value: PostingAction.RENEW,
									},
									{
										title: 'Deactivate',
										value: PostingAction.DEACTIVATE,
									},
							  ]
							: [
									{
										title: 'Activate',
										value: PostingAction.ACTIVATE,
									},
							  ]
					}
					sx={{
						position: 'fixed',
						bottom: 24,
						left: '50%',
						transform: 'translateX(-50%)',
						zIndex: isLtMd ? theme.zIndex.drawer - 1 : 1,
					}}
				/>
			) : null}
		</React.Fragment>
	);
};

export default JobPostingList;
